
/**
 * @file VDIComplianceForm.js
 *
 * @author - Team D (Meeramol Mathew, Krishnapriya N, Anju Sebastian)
 * @sprint - B2W-Sprint-3
 * @description -
 * This component manages the VDI compliance questionare as modal view.
 *
 */
import React, { useState, useEffect } from "react";
import { Modal, Button, Card, Checkbox, Spin } from "antd";




const VDIComplianceForm = ({ isOpen, onClose, onVDIComplianceCreate }) => {

    const [loading, setLoading] = useState(false);
    // List of all checkbox keys
    const checkboxKeys = [
        "email_attachment", "upload_code", "repositories_access", "business_info", "screen_sharing",
        "chatgpt_upload", "social_media", "spam_email", "phishing_report", "mandatory_trainings",
        "project_info", "password_sharing", "external_tools", "vdi_usage", "personal_info_sharing",
        "production_data", "data_copy_approval", "policy_violation", "legal_consequence", "non_compliance"
    ];
    // Function to get checkbox label text
    const getCheckboxLabel = (key) => {
        const labels = {
            email_attachment: <>Do not send any email with attachment or project information to anyone (including your own UST email ID) from the Walmart VDI.</>,
            upload_code: <>Do not upload code or any document created within the Walmart environment to <strong>Personal/private Git/cloud</strong> location, or any SharePoint.</>,
            repositories_access: <>Do not access <strong>Repositories</strong> (SharePoint, Confluence, etc.) not related to your project from VDI.</>,
            business_info: <> Do not share any business information outside <strong>VDI</strong>/Walmart systems.</>,
            screen_sharing: <>Do not share any customer info or screen share the VDI with people outside Walmart.</>,
            chatgpt_upload: <>Do not share/upload Walmart code snippets or business data in <strong>ChatGPT</strong>  or forums for optimization or doubt clarification.</>,
            social_media: <>Do not share Walmart <strong>logos/photos</strong> or customer names in any social media.</>,
            spam_email: <>Do not click any links/attachments or respond to <strong>Spam</strong> emails.</>,
            phishing_report: <>Report spam/phishing emails using <strong>"Phish Alert Report"</strong> option as per Infosec policy.</>,
            mandatory_trainings: <>All mandatory supplier trainings should be completed within the specified time.</>,
            project_info: <>Do not share your project information to anyone outside your project.</>,
            password_sharing: <>Do not share your Walmart <strong>password</strong> or any other password related to Walmart environment to anyone.</>,
            external_tools: <>Do not use external APIs/tools which can access Walmart information. Use only Walmart-approved external/internal resources.</>,
            vdi_usage: <>Do not use VDI for accessing personal emails or browsing non-technical contents and contents not related to project.</>,
            personal_info_sharing: <>Do not share/discuss personal information/concerns or UST internal/confidential matters with client.</>,
            production_data: <>Never copy production data to your laptop.</>,
            data_copy_approval: <>Never copy data from a production environment to a non-production environment without explicit email approval from Walmart Manager.</>,
            policy_violation: <>Any violation of these rules is deemed a <strong>serious offense</strong> and could result in <strong>disciplinary action</strong></>,
            legal_consequence: <>There will be <strong>Legal consequence</strong> if you share any Walmart info to outside (public site/forum/email).</>,
            non_compliance: <>Non-compliance to policy could result in <strong>termination</strong>/final settlements on hold.</>
        };
        return labels[key] || "Unknown checkbox";
    };

    // Initialize state with all checkboxes unchecked
    const [checkedItems, setCheckedItems] = useState(
        checkboxKeys.reduce((acc, key) => ({ ...acc, [key]: false }), {})
    );

    // Function to toggle checkbox state
    const handleCheckboxChange = (key) => {
        setCheckedItems((prevState) => ({
            ...prevState,
            [key]: !prevState[key]
        }));
    };

    // Check if all checkboxes are selected
    const allChecked = checkboxKeys.every((key) => checkedItems[key]);

    /**
        Handles the creation of VDI Compliance by making an API call.
        Displays a loading spinner while the request is in progress.
        Calls the API (`onVDIComplianceCreate`)** to submit compliance.
    **/

    const handleVDICreate = async () => {
        setLoading(true);
        try {
            const response = await onVDIComplianceCreate();
            if (response.ok) {
                onClose(); // Close the modal only if the API call is successful
            } else {
                throw new Error("Failed to save VDI Compliance.");
            }
        } catch (error) {
            console.error("Error creating VDI Compliance:", error);
        } finally {
            setTimeout(() => setLoading(false), 2000);
        }
    };


    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            okText="Read and Accept"
            onOk={handleVDICreate}
            okButtonProps={{
                className: "text-white border p-4 bg-[var(--color-primary)]",
                style: !allChecked ? {
                            color: 'white', // Set the text color to grey
                            cursor: 'not-allowed', // Change the cursor to indicate the button is disabled
                            opacity: 0.6 // Optional: Make the button appear slightly faded
                          } : {},
                disabled: !allChecked || loading, // Disable until all checkboxes are checked
            }}
            title={
                <header className="mx-auto  space-y-8">
                    <h2 className="text-primary font-bold text-center w-full">Walmart VDI Security Requirements</h2>
                </header>
            }
            cancelButtonProps={{
                className: "text-white border p-4 bg-[var(--color-primary)]",
                disabled: loading,
            }}

            className="!w-[80vw] !h-[82vh] !max-w-none overflow-hidden p-6 flex flex-col"
            // style={{

            //     maxWidth: "none", // Removes Ant Design's max-width restriction
            //     overflow: "hidden", // Prevents modal itself from scrolling
            // }}

            maskClosable={false} // Prevents closing on outside click
        >
            {/* <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"> */}
            <div className="flex flex-col items-center ">


                {/* <Card className="w-[70%] h-[75vh] p-6 overflow-y-auto"> */}
                <Card className="modal-content-wrapper" style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>

                    {/* Notice message */}
                    <p className="mb-4 text-sm text-gray-600">
                        Please ensure that you have read and checked all the checkboxes corresponding to security
                        requirements outlined below before proceeding to click on the <b>"Read and Accept"</b> button.
                    </p>

                    <div className="text-gray-600">
                        {/* Section 1: Sharing Information Outside VDI */}
                        <h3 className="font-semibold mt-4 mb-2">Sharing Information Outside VDI</h3>
                        {["email_attachment", "upload_code", "repositories_access", "business_info", "screen_sharing"].map((key) => (
                            <div key={key} className="mb-2">
                                <Checkbox checked={checkedItems[key]} onChange={() => handleCheckboxChange(key)}>
                                    <span className="text-gray-600">{getCheckboxLabel(key)}</span>
                                </Checkbox>
                            </div>
                        ))}

                        {/* Section 2: Publishing Customer Information */}
                        <h3 className="font-semibold mt-4 mb-2">Publishing Customer Information on Social Platforms</h3>
                        {["chatgpt_upload", "social_media"].map((key) => (
                            <div key={key} className="mb-2">
                                <Checkbox checked={checkedItems[key]} onChange={() => handleCheckboxChange(key)}>
                                    <span className="text-gray-600">{getCheckboxLabel(key)}</span>
                                </Checkbox>
                            </div>
                        ))}

                        {/* Section 3: Training and Security */}
                        <h3 className="font-semibold mt-4 mb-2">Training and Security</h3>
                        {["spam_email", "phishing_report", "mandatory_trainings", "project_info", "password_sharing", "external_tools"].map((key) => (
                            <div key={key} className="mb-2">
                                <Checkbox checked={checkedItems[key]} onChange={() => handleCheckboxChange(key)}>
                                    <span className="text-gray-600">{getCheckboxLabel(key)}</span>
                                </Checkbox>
                            </div>
                        ))}

                        {/* Section: Others */}
                        <h3 className="font-semibold mt-4 mb-2">Others</h3>
                        {["vdi_usage", "personal_info_sharing"].map((key) => (
                            <div key={key} className="mb-2">
                                <Checkbox checked={checkedItems[key]} onChange={() => handleCheckboxChange(key)}>
                                    <span className="text-gray-600">{getCheckboxLabel(key)}</span>
                                </Checkbox>
                            </div>
                        ))}

                        {/* Section: Key Data Security Principles */}
                        <h3 className="font-semibold mt-4 mb-2">Key Data Security Principles</h3>
                        {["production_data", "data_copy_approval", "policy_violation"].map((key) => (
                            <div key={key} className="mb-2">
                                <Checkbox checked={checkedItems[key]} onChange={() => handleCheckboxChange(key)}>
                                    <span className="text-gray-600">{getCheckboxLabel(key)}</span>
                                </Checkbox>
                            </div>
                        ))}

                        {/* Section: Legal Consequences */}
                        <h3 className="font-semibold mt-4 mb-2 text-red-600">Legal Consequences</h3>
                        {["legal_consequence", "non_compliance"].map((key) => (
                            <div key={key} className="mb-2">
                                <Checkbox checked={checkedItems[key]} onChange={() => handleCheckboxChange(key)}>
                                    <span className="text-red-600">{getCheckboxLabel(key)}</span>
                                </Checkbox>
                            </div>
                        ))}
                    </div>

                    {/* Buttons - Stick to bottom */}
                    {/* <div className="sticky bottom-0 left-0 right-0 bg-white py-4 flex justify-end gap-2 shadow-md border-t border-gray-200">
                    <Button onClick={onClose} className="px-4 py-2">Cancel</Button>
                    <Button type="primary" disabled={!allChecked} className="px-4 py-2">Read and Accept</Button>
                </div> */}
                </Card>
            </div>
            {loading && (
                <div className="fixed inset-0 flex justify-center items-center bg-white bg-opacity-75 z-50">
                    <Spin size="large" />
                </div>
            )}

        </Modal>
    );
};


export default VDIComplianceForm;
